import React, { MouseEventHandler, forwardRef, useState, useEffect } from 'react';
import { Room } from 'matrix-js-sdk';
import {
  Avatar,
  Box,
  Icon,
  IconButton,
  Icons,
  Text,
  Menu,
  MenuItem,
  config,
  PopOut,
  toRem,
  Line,
  RectCords,
  Badge,
} from 'folds';
import { useFocusWithin, useHover } from 'react-aria';
import FocusTrap from 'focus-trap-react';
import { NavItem, NavItemContent, NavItemOptions, NavLink } from '../../components/nav';
import { UnreadBadge, UnreadBadgeCenter } from '../../components/unread-badge';
import { RoomAvatar, RoomIcon } from '../../components/room-avatar';
import { getDirectRoomAvatarUrl, getRoomAvatarUrl } from '../../utils/room';
import { nameInitials } from '../../utils/common';
import { useMatrixClient } from '../../hooks/useMatrixClient';
import { useRoomUnread } from '../../state/hooks/unread';
import { roomToUnreadAtom } from '../../state/room/roomToUnread';
import { usePowerLevels, usePowerLevelsAPI } from '../../hooks/usePowerLevels';
import { copyToClipboard } from '../../utils/dom';
import { markAsRead } from '../../../client/action/notifications';
import { openInviteUser, toggleRoomSettings } from '../../../client/action/navigation';
import { UseStateProvider } from '../../components/UseStateProvider';
import { LeaveRoomPrompt } from '../../components/leave-room-prompt';
import { useRoomTypingMember } from '../../hooks/useRoomTypingMembers';
import { TypingIndicator } from '../../components/typing-indicator';
import { stopPropagation } from '../../utils/keyboard';
import { getMatrixToRoom } from '../../plugins/matrix-to';
import { getCanonicalAliasOrRoomId, isRoomAlias } from '../../utils/matrix';
import { getViaServers } from '../../plugins/via-servers';
import { useMediaAuthentication } from '../../hooks/useMediaAuthentication';
import { useRoomMembers } from '../../hooks/useRoomMembers';
import { Membership } from '../../../types/matrix/room';
import Chip from '../../atoms/chip/Chip';
import { useRoomProcessInfo } from '../../state/room/roomProcessInfo';
import navigation from '../../../client/state/navigation';
import cons from '../../../client/state/cons';

import * as css from './styles.css';

const getProcessStatusDisplayProps = (processInfo) => {
  if (!processInfo || typeof processInfo.process_action !== 'number') {
    return {
      text: '待處理',
      backgroundColor: 'var(--curve-status-pending-bg)',
      textColor: 'var(--curve-status-pending-text)',
    };
  }

  if (processInfo.process_action === 0) {
    if (processInfo.process_state === 0) {
      return {
        text: '待處理',
        backgroundColor: 'var(--curve-status-pending-bg)',
        textColor: 'var(--curve-status-pending-text)',
      };
    }
    if (processInfo.process_state === 1) {
      return {
        text: '處理中',
        backgroundColor: 'var(--curve-status-inprog-bg)',
        textColor: 'var(--curve-status-inprog-text)',
      };
    }
  } else if (processInfo.process_action === 1) {
    return {
      text: '轉問題紀錄',
      backgroundColor: 'var(--curve-status-action1-bg)',
      textColor: 'var(--curve-status-action1-text)',
    };
  } else if (processInfo.process_action === 2) {
    return {
      text: '轉需求紀錄',
      backgroundColor: 'var(--curve-status-action2-bg)',
      textColor: 'var(--curve-status-action2-text)',
    };
  } else if (processInfo.process_action === 3) {
    return {
      text: '已結案',
      backgroundColor: 'var(--curve-status-closed-alt-bg)',
      textColor: 'var(--curve-status-closed-alt-text)',
    };
  }
  return {
    text: '待處理',
    backgroundColor: 'var(--curve-status-pending-bg)',
    textColor: 'var(--curve-status-pending-text)',
  };
};

type RoomNavItemMenuProps = {
  room: Room;
  requestClose: () => void;
};
const RoomNavItemMenu = forwardRef<HTMLDivElement, RoomNavItemMenuProps>(
  ({ room, requestClose }, ref) => {
    const mx = useMatrixClient();
    const unread = useRoomUnread(room.roomId, roomToUnreadAtom);
    const powerLevels = usePowerLevels(room);
    const { getPowerLevel, canDoAction } = usePowerLevelsAPI(powerLevels);
    const canInvite = canDoAction('invite', getPowerLevel(mx.getUserId() ?? ''));

    const handleMarkAsRead = () => {
      markAsRead(mx, room.roomId);
      requestClose();
    };

    const handleInvite = () => {
      openInviteUser(room.roomId);
      requestClose();
    };

    const handleCopyLink = () => {
      const roomIdOrAlias = getCanonicalAliasOrRoomId(mx, room.roomId);
      const viaServers = isRoomAlias(roomIdOrAlias) ? undefined : getViaServers(room);
      copyToClipboard(getMatrixToRoom(roomIdOrAlias, viaServers));
      requestClose();
    };

    const handleRoomSettings = () => {
      toggleRoomSettings(room.roomId);
      requestClose();
    };

    return (
      <Menu ref={ref} style={{ maxWidth: toRem(160), width: '100vw' }}>
        <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
          <MenuItem
            onClick={handleMarkAsRead}
            size="300"
            after={<Icon size="100" src={Icons.CheckTwice} />}
            radii="300"
            disabled={!unread}
          >
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Mark as Read
            </Text>
          </MenuItem>
        </Box>
        { /* <Line variant="Surface" size="300" /> */}
        <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
          { /* <MenuItem
            onClick={handleInvite}
            variant="Primary"
            fill="None"
            size="300"
            after={<Icon size="100" src={Icons.UserPlus} />}
            radii="300"
            disabled={!canInvite}
          >
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Invite
            </Text>
          </MenuItem> */ }
          { /* <MenuItem
            onClick={handleCopyLink}
            size="300"
            after={<Icon size="100" src={Icons.Link} />}
            radii="300"
          >
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Copy Link
            </Text>
          </MenuItem> */ }
          { /* <MenuItem
            onClick={handleRoomSettings}
            size="300"
            after={<Icon size="100" src={Icons.Setting} />}
            radii="300"
          >
            <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
              Room Settings
            </Text>
          </MenuItem> */ }
        </Box>
        <Line variant="Surface" size="300" />
        <Box direction="Column" gap="100" style={{ padding: config.space.S100 }}>
          <UseStateProvider initial={false}>
            {(promptLeave, setPromptLeave) => (
              <>
                <MenuItem
                  onClick={() => setPromptLeave(true)}
                  variant="Critical"
                  fill="None"
                  size="300"
                  after={<Icon size="100" src={Icons.ArrowGoLeft} />}
                  radii="300"
                  aria-pressed={promptLeave}
                >
                  <Text style={{ flexGrow: 1 }} as="span" size="T300" truncate>
                    Leave Room
                  </Text>
                </MenuItem>
                {promptLeave && (
                  <LeaveRoomPrompt
                    roomId={room.roomId}
                    onDone={requestClose}
                    onCancel={() => setPromptLeave(false)}
                  />
                )}
              </>
            )}
          </UseStateProvider>
        </Box>
      </Menu>
    );
  }
);

type RoomNavItemProps = {
  room: Room;
  selected: boolean;
  linkPath: string;
  muted?: boolean;
  showAvatar?: boolean;
  direct?: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement> | undefined;
  priority?: string;
  priorityColor?: string;
  priorityTextColor?: string;
};
export function RoomNavItem({
  room,
  selected,
  showAvatar,
  direct,
  muted,
  linkPath,
  onClick,
  priority,
  priorityColor,
  priorityTextColor,
}: RoomNavItemProps) {
  const mx = useMatrixClient();
  const useAuthentication = useMediaAuthentication();
  const [hover, setHover] = useState(false);
  const { hoverProps } = useHover({ onHoverChange: setHover });
  const { focusWithinProps } = useFocusWithin({ onFocusWithinChange: setHover });
  const [menuAnchor, setMenuAnchor] = useState<RectCords>();
  const unread = useRoomUnread(room.roomId, roomToUnreadAtom);
  const typingMember = useRoomTypingMember(room.roomId);
  const { info: processInfo, fetchInfo: fetchProcessInfo } = useRoomProcessInfo(mx, room.roomId);
  const [creatorProject, setCreatorProject] = useState<string>("");

  // 1. Define a type for profile info that includes 'project'.
  type ProfileWithProject = {
    avatar_url?: string;
    displayname?: string;
    project?: string;
    // Allow unknown extra fields, but avoid 'any'
    [key: string]: unknown;
  };

  useEffect(() => {
    fetchProcessInfo();
    const handleProcessInfoUpdate = (roomId) => {
      if (room.roomId === roomId) {
        fetchProcessInfo();
      }
    };
    navigation.on(cons.events.navigation.PROCESS_INFO_UPDATED, handleProcessInfoUpdate);
    return () => {
      navigation.removeListener(cons.events.navigation.PROCESS_INFO_UPDATED, handleProcessInfoUpdate);
    };
  }, [room.roomId, fetchProcessInfo]);

  useEffect(() => {
    const fetchCreatorProject = async () => {
      try {
        const createEvent = room.currentState.getStateEvents("m.room.create", "");
        const creatorId = createEvent?.getContent()?.creator;
        if (creatorId) {
          const profile = await mx.getProfileInfo(creatorId) as ProfileWithProject;
          if (profile && typeof profile.project === "string") {
            setCreatorProject(profile.project);
          } else {
            setCreatorProject("");
          }
        } else {
          setCreatorProject("");
        }
      } catch (err) {
        setCreatorProject("");
      }
    };
    fetchCreatorProject();
  }, [room, mx]);

  // Determine status display properties using processInfo
  const processStatusDisplayProps = getProcessStatusDisplayProps(processInfo);

  const handleContextMenu: MouseEventHandler<HTMLElement> = (evt) => {
    evt.preventDefault();
    setMenuAnchor({
      x: evt.clientX,
      y: evt.clientY,
      width: 0,
      height: 0,
    });
  };

  const handleOpenMenu: MouseEventHandler<HTMLButtonElement> = (evt) => {
    setMenuAnchor(evt.currentTarget.getBoundingClientRect());
  };

  const optionsVisible = hover || !!menuAnchor;

  return (
    <NavItem
      className={css.RoomNavItem}
      title={room.name}
      variant="Background"
      radii="400"
      highlight={unread !== undefined}
      aria-selected={selected}
      data-hover={!!menuAnchor}
      onContextMenu={handleContextMenu}
      onClick={onClick}
      {...hoverProps}
      {...focusWithinProps}
    >
      <NavLink to={linkPath}>
        <NavItemContent>
          <Box as="span" grow="Yes" alignItems="Center" gap="200">
            {priority && (
              <Text as="span" style={{
                backgroundColor: priorityColor,
                minWidth: '28px',
                padding: '2px 4px',
                borderRadius: '4px',
                textAlign: 'center',
                fontSize: '10px',
                color: priorityTextColor ?? 'var(--curve-text-dark)',
              }}>
                {priority}
              </Text>
            )}
            <Avatar size="300" radii="400" style={{ width: '60px', height: '40px' }}>
              {showAvatar ? (
                <RoomAvatar
                  roomId={room.roomId}
                  src={
                    direct ? getDirectRoomAvatarUrl(mx, room, 96, useAuthentication) : getRoomAvatarUrl(mx, room, 96, useAuthentication)
                  }
                  alt={room.name}
                  renderFallback={() => (
                    <Text as="span" style={{ fontSize: '10px' }}>
                      {creatorProject}
                      {/*nameInitials(creatorProject)*/}
                    </Text>
                  )}
                />
              ) : (
                <RoomIcon
                  style={{ opacity: unread ? config.opacity.P500 : config.opacity.P300 }}
                  filled={selected}
                  size="100"
                  joinRule={room.getJoinRule()}
                />
              )}
            </Avatar>
            <Box as="span" grow="Yes">
              <Text priority={unread ? '500' : '300'} as="span" size="Inherit" truncate style={{ maxWidth: '250px', display: 'inline-block' }}>
                {room.name}
              </Text>
              {/* 狀態標籤：依 errorC003.png，顯示 chip 並正確判斷狀態 */}
              {processStatusDisplayProps && (
                <Box as="span" style={{ display: 'inline-flex', justifyContent: 'flex-end', minWidth: '60px', maxWidth: '80px', marginLeft: '8px' }}>
                  <Chip
                    text={processStatusDisplayProps.text}
                    backgroundColor={processStatusDisplayProps.backgroundColor}
                    textColor={processStatusDisplayProps.textColor}
                  />
                </Box>
              )}
            </Box>
            {!optionsVisible && !unread && !selected && typingMember.length > 0 && (
              <Badge size="300" variant="Secondary" fill="Soft" radii="Pill" outlined>
                <TypingIndicator size="300" disableAnimation />
              </Badge>
            )}
            {!optionsVisible && unread && (
              <UnreadBadgeCenter>
                <UnreadBadge highlight={unread.highlight > 0} count={unread.total} />
              </UnreadBadgeCenter>
            )}
            {muted && !optionsVisible && <Icon size="50" src={Icons.BellMute} />}
          </Box>
        </NavItemContent>
      </NavLink>
      {optionsVisible && (
        <NavItemOptions>
          <PopOut
            anchor={menuAnchor}
            offset={menuAnchor?.width === 0 ? 0 : undefined}
            alignOffset={menuAnchor?.width === 0 ? 0 : -5}
            position="Bottom"
            align={menuAnchor?.width === 0 ? 'Start' : 'End'}
            content={
              <FocusTrap
                focusTrapOptions={{
                  initialFocus: false,
                  returnFocusOnDeactivate: false,
                  onDeactivate: () => setMenuAnchor(undefined),
                  clickOutsideDeactivates: true,
                  isKeyForward: (evt: KeyboardEvent) => evt.key === 'ArrowDown',
                  isKeyBackward: (evt: KeyboardEvent) => evt.key === 'ArrowUp',
                  escapeDeactivates: stopPropagation,
                }}
              >
                <RoomNavItemMenu room={room} requestClose={() => setMenuAnchor(undefined)} />
              </FocusTrap>
            }
          > { /*
            <IconButton
              onClick={handleOpenMenu}
              aria-pressed={!!menuAnchor}
              variant="Background"
              fill="None"
              size="300"
              radii="300"
            >
              <Icon size="50" src={Icons.VerticalDots} />
            </IconButton>
            */ }
          </PopOut>
        </NavItemOptions>
      )}
    </NavItem>
  );
}
